import { InputBits } from "../game/Types";
import { mapPickerTemplate } from "./MapPicker";

const logoMark = (className = ""): string => `
  <svg class="brand-mark ${className}" viewBox="0 0 52 44" aria-hidden="true">
    <path class="logo-skull" d="M15 19h22v13l-5 5v4h-5v-5h-3v5h-5v-4l-4-5z"/>
    <path class="logo-hat" d="M10 16h32l6 5c-5 4-39 4-44 0zm8-13h16l6 13H13z"/>
    <path class="logo-cut" d="M20 25h5v4h-5zm8 0h5v4h-5zm-5 8h7v2h-7z"/>
  </svg>`;

export const appTemplate = (): string => `
  <main id="shell" class="shell">
    <header class="topbar">
      <a class="brand" href="/" aria-label="Mini Doom home">
        ${logoMark()}
        <span class="brand-type"><small>MINI</small><b>DOOM</b></span>
      </a>
      <div class="network-pill"><span id="network-dot"></span><span id="network-label">P2P ready</span></div>
    </header>

    <section id="landing" class="landing">
      <div class="hero-copy">
        <p class="eyebrow">PEER-TO-PEER MAYHEM</p>
        <h1>Small arena.<br><em>Big doom.</em></h1>
        <p class="lede">Mini Doom is a rollback-powered western arena shooter running entirely between browsers. Name your gunslinger and make sparks.</p>
        <label class="field-label" for="player-name">PLAYER NAME</label>
        <div class="callsign-editor">
          <input id="player-name" class="callsign" maxlength="18" autocomplete="nickname" value="Gunslinger" />
          <button id="save-name" class="name-save" type="button">Save name</button>
        </div>
        <fieldset class="gender-field">
          <legend>GENDER</legend>
          <label><input type="radio" name="player-gender" value="male" checked /><span>Male</span></label>
          <label><input type="radio" name="player-gender" value="female" /><span>Female</span></label>
        </fieldset>
        <fieldset class="network-mode" aria-label="Room network">
          <label><input type="radio" name="network-mode" value="online" checked /><span><b>Online</b><small>Internet rooms</small></span></label>
          <label><input type="radio" name="network-mode" value="lan" /><span><b>LAN</b><small>Same Wi-Fi</small></span></label>
        </fieldset>
        <div class="hero-actions">
          <button id="create-room" class="button primary">Create room</button>
          <button id="practice" class="button quiet">Practice vs bots · Offline</button>
        </div>
      </div>

      <aside class="dispatch">
        <div class="dispatch-head">
          <span id="frequency-label">ONLINE FREQUENCIES</span>
          <div class="dispatch-tools">
            <button id="nearby-rooms" class="text-button" type="button">Nearby</button>
            <button id="refresh-lobbies" class="text-button" type="button">Refresh</button>
          </div>
        </div>
        <div class="join-line">
          <input id="join-code" maxlength="5" placeholder="ROOM CODE" aria-label="Room code" />
          <button id="join-room" class="button compact">Join</button>
        </div>
        <div id="lobby-list" class="lobby-list">
          <p class="empty-state">Scanning for available rooms…</p>
        </div>
        <p id="landing-message" class="notice-line" aria-live="polite"></p>
        <p id="landing-error" class="error-line" role="alert"></p>
      </aside>

      <section class="game-details" aria-labelledby="about-mini-doom">
        <div class="details-intro">
          <p class="eyebrow">PLAY IN YOUR BROWSER</p>
          <h2 id="about-mini-doom">Arena combat.<br>No download.</h2>
          <p>Start a solo bot match instantly or create a room for up to eight gunslingers. Mini Doom combines quick western firefights with responsive rollback networking.</p>
        </div>
        <div class="feature-grid" aria-label="Mini Doom features">
          <article>
            <span>01</span>
            <h3>Instant practice</h3>
            <p>Learn the weapons, movement, and maps in a solo match against bots.</p>
          </article>
          <article>
            <span>02</span>
            <h3>2–8 player rooms</h3>
            <p>Create a browser-based match, share the room code, and play deathmatch or team battle.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Rollback multiplayer</h3>
            <p>Peer-to-peer WebRTC and deterministic rollback keep online matches fast and responsive.</p>
          </article>
        </div>
        <div class="game-faq">
          <h2>Mini Doom FAQ</h2>
          <details>
            <summary>Can I play without downloading anything?</summary>
            <p>Yes. Mini Doom runs in a modern web browser with no game download or account required.</p>
          </details>
          <details>
            <summary>How many players can join?</summary>
            <p>Online rooms support 2–8 players. You can also start a solo practice match against bots.</p>
          </details>
          <details>
            <summary>How does online multiplayer work?</summary>
            <p>Matches use peer-to-peer WebRTC connections and rollback networking. Signaling helps players connect but does not run the match simulation.</p>
          </details>
        </div>
      </section>
    </section>

    <section id="room" class="room hidden">
      <div class="room-title">
        <div class="room-titlebar">
          <p class="eyebrow">PRIVATE DOOM ROOM</p>
          <button id="leave-room" class="room-exit" type="button" aria-label="Leave room and return home">
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M8.5 4 3 10l5.5 6M3.5 10H17"/></svg>
            <span>Leave room</span>
          </button>
        </div>
        <h2 id="room-name">Open arena</h2>
        <button id="copy-code" class="room-code" title="Copy room code"><span>CODE</span><b id="room-code">-----</b></button>
      </div>
      <div class="room-grid">
        <div>
          <p class="section-label">CONNECTED PLAYERS</p>
          <div id="room-peers" class="peer-list"></div>
          <div id="join-requests" class="join-requests hidden" aria-live="polite">
            <p class="section-label">ACCESS REQUESTS</p>
            <div id="join-request-list"></div>
          </div>
        </div>
        <div class="match-settings">
          <p class="section-label">MATCH LOADOUT</p>
          <label>MODE<select id="match-mode"><option value="deathmatch">Deathmatch</option><option value="teams">Team battle</option></select></label>
          <div class="match-map-field"><span class="match-map-label">MAP</span>${mapPickerTemplate()}</div>
          <label>PLAYERS<select id="max-players"><option>2</option><option>4</option><option selected>6</option><option>8</option></select></label>
          <p id="room-status" class="room-status">Opening peer channels…</p>
          <button id="retry-links" class="text-button retry-links hidden">Retry peer links</button>
          <button id="start-match" class="button primary wide" disabled>Start match</button>
        </div>
      </div>
    </section>
  </main>

  <aside id="tweak-panel" class="tweak-panel" aria-label="Display tweaks">
    <span>TWEAK</span>
    <div><b>Visual</b><button data-visual="crt" class="active">CRT</button><button data-visual="clean">Clean</button></div>
    <div><b>Camera</b><button data-camera="punchy">Punchy</button><button data-camera="steady" class="active">Steady</button></div>
    <div><b>Audio</b><button data-audio="on" class="active">On</button><button data-audio="mute">Mute</button></div>
    <div><b>Screen</b><button class="fullscreen-toggle" data-fullscreen type="button" aria-keyshortcuts="Control+Shift+F" aria-pressed="false">FULL</button></div>
  </aside>
`;

export const gameTemplate = (): string => `
  <div id="game-root"></div>
  <div class="game-vignette"></div>
  <header class="game-topbar">
    <div class="game-brand" aria-label="Mini Doom">${logoMark("compact")}</div>
    <div id="hud-health-stat" class="hud-stat"><span id="hud-health-label">HEALTH</span><b id="hud-health">100</b></div>
    <div class="hud-stat weapon-stat"><span>WEAPON</span><b id="hud-weapon">Pistol</b></div>
    <div id="hud-ammo-stat" class="hud-stat ammo-stat"><span id="hud-ammo-label">AMMO</span><b id="hud-ammo">12 / 12</b></div>
    <div class="hud-stat"><span>K / D</span><b id="hud-score">0 / 0</b></div>
    <div class="hud-stat net"><span>LINK</span><b id="hud-net">—</b></div>
    <button id="leave-match" class="leave-button">Exit</button>
  </header>
  <div id="scoreboard" class="scoreboard">
    <div class="score-head"><span>PLAYER</span><b>K</b><i>D</i></div>
    <div id="scoreboard-rows"></div>
    <small>Hold TAB to inspect</small>
  </div>
  <div class="controls-hint" aria-label="Game controls">
    <span><b>FIRE</b> hold <kbd>SPACE</kbd></span>
    <span><b>RELOAD</b> <kbd>R</kbd></span>
    <span><b>JUMP</b> <kbd>W</kbd> / <kbd>↑</kbd></span>
    <span><b>WEAPON</b> <kbd>1–5</kbd> / wheel</span>
  </div>
  <div id="death-feedback" class="death-feedback hidden" role="status" aria-live="assertive">
    <p id="death-source">ELIMINATED</p>
    <h2>YOU DIED</h2>
    <div class="death-respawn"><span>REDEPLOYING IN</span><b><i id="respawn-seconds">3</i>S</b></div>
  </div>
  <div id="winner" class="winner hidden" role="dialog" aria-live="assertive">
    ${logoMark("result-mark")}
    <p id="winner-kicker">VICTORY</p>
    <h2 id="winner-name">YOU WIN</h2>
    <div class="result-summary"><span id="winner-detail">You own the arena.</span><b id="winner-score">10 K · 2 D</b></div>
    <button id="winner-exit" class="button primary">Back to lobby</button>
  </div>
  ${touchControls()}
  <aside id="tweak-panel" class="tweak-panel in-game" aria-label="Display tweaks">
    <span>TWEAK</span>
    <div><b>Visual</b><button data-visual="crt" class="active">CRT</button><button data-visual="clean">Clean</button></div>
    <div><b>Camera</b><button data-camera="punchy">Punchy</button><button data-camera="steady" class="active">Steady</button></div>
    <div><b>Audio</b><button data-audio="on" class="active">On</button><button data-audio="mute">Mute</button></div>
    <div><b>Screen</b><button class="fullscreen-toggle" data-fullscreen type="button" aria-keyshortcuts="Control+Shift+F" aria-pressed="false">FULL</button></div>
  </aside>
`;

const touchControls = (): string => `
  <div class="touch-controls" aria-label="Touch controls">
    <div class="touch-move">
      <button data-input-bit="${InputBits.left}" aria-label="Move left">←</button>
      <button data-input-bit="${InputBits.right}" aria-label="Move right">→</button>
    </div>
    <div class="touch-action">
      <button data-input-bit="${InputBits.jump}" aria-label="Jump">JMP</button>
      <button data-input-bit="${InputBits.dash}" aria-label="Dash">DSH</button>
      <button data-input-bit="${InputBits.melee}" aria-label="Melee">HIT</button>
      <button data-input-bit="${InputBits.reload}" aria-label="Reload">RLD</button>
    </div>
  </div>
`;
